import React, { useEffect, useMemo } from 'react';

import styles from '../../ManageDealDrawer.module.scss';
import { convertToFirstLetterCapital, isEmpty } from '../../../../utils/utils';
import {
    CONFIG_STEP,
    CONFIG_TYPE,
    CUSTOM_COMPONENT_MAP,
    DEBT_PRODUCT_FILTERS,
    ERROR_MESSAGES,
    IMPORTANCE_LEVELS,
    PEC_IMPORTANCE_CONSTANTS,
    RANGE_REQUIREMENT,
    UPLOAD_FILES_PREFIXES,
} from '../../DealDrawerConstants';
import Dropdown from '../../../_custom/Dropdown';
import MultiSelectDropdown from '../../../_custom/MultiSelectDropdown/MultiSelectDropdown';
import { Button, Input } from '../../../_custom';
import InvestorDealShare from '../../InvestorDealShare/InvestorDealShare';
import UploadCard from '../../../../modules/Company/UploadJourney/components/UploadCard/UploadCard';
import DealDrawerButtonComponents from '../../DealDrawerButtonComponents/DealDrawerButtonComponents';
import MultiSelectSearchDropdown from '../MultiSelectSearchDropdown/MultiSelectSearchDropdown';
import { getDataByConfig } from '../../ManageDealDrawerHelper';
import { fetchDataByConfig } from '../../ManageDealDrawerService';
import { polusBaseUrl } from '../../../../utils/axios-interceptor';
import { useAppSelector } from '../../../../app/hooks';
import { RenderModal } from './RenderModal';
import PlusIcon from '../../../../assets/Svg/PlusIcon';
import { DATE_FORMATS, getFiscalYearSuffix } from '../../../../utils/dateUtils';
import { Mixpanel } from '../../../../utils/mixpanel';
import { ESTIMATE_METRIC } from '../../../../utils/constants/mixpanelEvents/companyEvents';
import { DatePicker } from '../../../_custom/DatePicker/DatePicker';
import CalendarIcon from '../../../../assets/Svg/CalendarIcon';
import CategorySelector from '../../../CategorySelector/CategorySelector';

interface InputRendererProps {
    sectionKey: string;
    fieldKey: string;
    config: any;
    formData: Record<string, any>;
    handleChange: (key: string, value: any, importanceKey?: string | null) => void;
    dealDrawerState: any;
    lenderList?: { id: string; name: string }[];
    loanTypeOptions?: Record<string, any>;
    sectorList?: any[];
    negativeSectorList?: any[];
    source?: string;
    step?: number;
    setDisablePrimary?: (value: boolean) => void;
    auditedfinancialsMapping: Record<string, { year?: number; value: string }>;
    isWebsiteValid?: boolean;
    setIsWebsiteValid?: (value: boolean) => void;
    getInputValidation?: (value: string, key: string) => boolean;
    getButtonIcon?: (text: string) => JSX.Element;
    getButtonText?: () => string;
    validateWebsite?: (value: string) => void;
    isWebsitePrefilled?: boolean;
    negativeLenderList?: any[];
    getConvertedValue?: (value: string, unit: string) => string;
    showFloatingLabel?: boolean;
    citiesList?: { label: string; value: string }[];
    activeFinancialsTab?: string;
    sectionDualDataStorage?: boolean;
    handleFinancialsTabChange?: (tab: string) => void;
}

export const InputRenderer: React.FC<InputRendererProps> = ({
    sectionKey,
    fieldKey,
    config,
    source,
    step,
    loanTypeOptions,
    handleChange,
    dealDrawerState,
    lenderList,
    formData,
    sectorList,
    negativeSectorList,
    setDisablePrimary,
    auditedfinancialsMapping,
    isWebsiteValid,
    getInputValidation,
    getButtonIcon,
    getButtonText,
    validateWebsite,
    setIsWebsiteValid,
    isWebsitePrefilled,
    negativeLenderList,
    getConvertedValue,
    showFloatingLabel = true,
    citiesList,
    activeFinancialsTab,
    sectionDualDataStorage,
    handleFinancialsTabChange,
}) => {
    /**
     * Builds the formData key for this field
     *
     * For sections with dual data storage (e.g., borrowerFinancials):
     * - Data fields → borrowerFinancials.actualData.annualRevenue or borrowerFinancials.discussion.annualRevenue
     *
     * For regular sections → borrowerProfile.companySector
     */
    const formKey = useMemo(() => {
        let key = sectionKey ? `${sectionKey}.${fieldKey}` : fieldKey;

        // Early return if section doesn't support dual data storage
        if (!sectionDualDataStorage) return key;

        // Check if field is a controller (stays at parent level)
        const isParentKey = config?.isParentKey === true;
        // Data fields nest under actualData or discussion
        if (!isParentKey && activeFinancialsTab) {
            return `${sectionKey}.${activeFinancialsTab}.${fieldKey}`;
        }
        // Controller fields stay at parent level
        return `${sectionKey}.${fieldKey}`;
    }, [sectionKey, fieldKey, activeFinancialsTab, config?.isParentKey, sectionDualDataStorage]);

    const importanceKey = sectionKey
        ? `${sectionKey}.importance${convertToFirstLetterCapital(fieldKey)}`
        : `importance${convertToFirstLetterCapital(fieldKey)}`;
    const commentsThreadState = useAppSelector((state) => state.commentsThreadReducer);
    const user = useAppSelector((state) => state.user);
    const drawerContent = useAppSelector((state) => state.investor.dealApplicationDrawer);
    const [options, setOptions] = React.useState<{ label: string; value: string }[]>([]);
    const [showModal, setShowModal] = React.useState(false);

    // Check if we need to show an importance dropdown
    const hasImportance = config.importance === true;

    const fetchData = async () => {
        const res = await getDataByConfig(
            {
                parentOrgId: user.userData.investorOrgId,
            },
            config.api,
            'GET',
        );
        if (res) {
            let temp: any = [];
            Object.keys(res.lenderMap.ADMIN).forEach((key) => {
                temp.push({ label: res?.lenderMap?.ADMIN[key], value: key });
            });
            setOptions(temp);
        }
    };

    // set default value from config if formData for this key is empty and has defaultValue in config
    useEffect(() => {
        if (isEmpty(formData[formKey]) && config?.defaultValue) {
            handleChange(formKey, config.defaultValue);
        }
    }, [formKey, config?.defaultValue]);

    const validateRange = (
        minValue: string | undefined,
        maxValue: string | undefined,
        hasImportance: boolean,
        importanceValue?: string,
    ): {
        isValid: boolean;
        minErrorMessage: string;
        maxErrorMessage: string;
        importanceMissing: boolean;
        minIsValid: boolean;
        maxIsValid: boolean;
    } => {
        // Initialize with default values
        const result = {
            isValid: true,
            minErrorMessage: '',
            maxErrorMessage: '',
            importanceMissing: false,
            minIsValid: true,
            maxIsValid: true,
        };

        // Check min/max values relationship when both exist
        if (!isEmpty(minValue) && !isEmpty(maxValue)) {
            const minMaxValid = Number(minValue) <= Number(maxValue);

            if (!minMaxValid) {
                result.isValid = false;
                result.minErrorMessage = ERROR_MESSAGES.INVALID_RANGE;
                result.maxErrorMessage = ERROR_MESSAGES.INVALID_RANGE;
                result.minIsValid = false;
                result.maxIsValid = false;
                setDisablePrimary?.(true);
            } else if (hasImportance && !importanceValue) {
                result.importanceMissing = true;
            }
        }

        // Only perform required field validation if the field is required
        if (config.isRequired) {
            const rangeRequirement = config.rangeRequirement || RANGE_REQUIREMENT.BOTH;

            // Validate based on range requirement type
            switch (rangeRequirement) {
                case RANGE_REQUIREMENT.MIN:
                    if (isEmpty(minValue)) {
                        result.isValid = false;
                        result.minErrorMessage = ERROR_MESSAGES.REQUIRED;
                        result.minIsValid = false;
                        setDisablePrimary?.(true);
                    }
                    break;

                case RANGE_REQUIREMENT.MAX:
                    if (isEmpty(maxValue)) {
                        result.isValid = false;
                        result.maxErrorMessage = ERROR_MESSAGES.REQUIRED;
                        result.maxIsValid = false;
                        setDisablePrimary?.(true);
                    }
                    break;

                case RANGE_REQUIREMENT.BOTH:
                default:
                    if (isEmpty(minValue)) {
                        result.isValid = false;
                        result.minErrorMessage = ERROR_MESSAGES.REQUIRED;
                        result.minIsValid = false;
                        setDisablePrimary?.(true);
                    }
                    if (isEmpty(maxValue)) {
                        result.isValid = false;
                        result.maxErrorMessage = ERROR_MESSAGES.REQUIRED;
                        result.maxIsValid = false;
                        setDisablePrimary?.(true);
                    }
                    break;
            }
        }

        return result;
    };

    useEffect(() => {
        if (config.api && source === DEBT_PRODUCT_FILTERS) {
            fetchData();
        }
    }, [config.api]);

    useEffect(() => {
        if (config.api && !config.dropdownOptions?.length) {
            fetchDataByConfig({ aggregatorId: user.userData.investorOrgId }, config.api, 'GET', polusBaseUrl)
                .then((response) => {
                    const data = response?.data?.responseBody?.data;
                    if (data) {
                        const apiOptions = Object.values(data).map((item: any) => ({
                            label: item.productName || item.label || item.name || '',
                            value: item.productName || item.label || item.name || '',
                        }));
                        setOptions(apiOptions);
                    }
                })
                .catch((error) => {
                    console.error('Error fetching API options:', error);
                });
        }
    }, [config.api]);

    useEffect(() => {
        let options: { value: string; label: string }[] = [];
        if (formKey === 'dealTerms.loanTypes' || formKey === 'preferredTerms.loanTypes') {
            const loanTypeOptionsTransfomred = Object.values(loanTypeOptions || []).map((item: any) => ({
                value: item.productName,
                label: item.productName,
            }));
            options = loanTypeOptionsTransfomred;
        } else if (formKey === 'dealTerms.negativeLenderList') {
            options =
                negativeLenderList?.map((item) => ({
                    label: item.label,
                    value: item.label,
                })) || [];
        }
        setOptions(options);
    }, [sectionKey, loanTypeOptions, negativeLenderList]);

    switch (config?.type) {
        case CONFIG_TYPE.SEARCH_DROPDOWN:
        case CONFIG_TYPE.DROPDOWN: {
            // Example logic for a basic dropdown
            let value = formData[formKey];
            if (config.isSystemGenerated && auditedfinancialsMapping?.[formKey] && auditedfinancialsMapping[formKey]?.value !== value) {
                handleChange(formKey, auditedfinancialsMapping[formKey]?.value, hasImportance ? importanceKey : null);
                value = auditedfinancialsMapping[formKey]?.value;
            }
            const hasError =
                !!formData[formKey] && !formData[importanceKey] && formData[importanceKey] !== PEC_IMPORTANCE_CONSTANTS.NOT_NEEDED;
            return (
                <div className={styles.Dflex} style={{ width: '100%' }}>
                    <div className={styles.DflexColumn} style={{ width: hasImportance ? '70%' : '100%', gap: '0.25rem' }}>
                        <Dropdown
                            enableSearch={config.type === CONFIG_TYPE.SEARCH_DROPDOWN}
                            selectedOption={{
                                id: formData[formKey],
                                label: formData[formKey],
                            }}
                            optionsList={(config.dropdownOptions || []).map((opt: any) => ({
                                id: opt._id,
                                label: opt.label,
                            }))}
                            placeholder={config.isRequired ? config.placeholder : `${config.placeholder} `}
                            label={config.isRequired ? config.placeholder : `${config.placeholder} `}
                            itemClickHandler={(option) => {
                                handleChange(formKey, option.id, hasImportance ? importanceKey : null);
                                if (!option.id && hasImportance && formData[importanceKey]) handleChange(importanceKey, '');
                            }}
                            style={{ width: '100%', height: '44px' }}
                            disabled={config?.readonly ?? dealDrawerState?.readonly}
                            errorMessage={config.errorMessage}
                            showFloatingLabel={showFloatingLabel}
                        />
                        {/* {auditedfinancialsMapping[formKey] && (
                            <span className={styles.financialYearLabel}>
                                {auditedfinancialsMapping[formKey]?.year
                                    ? `Audited Data FY’${getFiscalYearSuffix(auditedfinancialsMapping[formKey].year)}: `
                                    : 'AICA Report: '}
                                <span>{`${auditedfinancialsMapping[formKey]?.value ?? ''}${config.unit ? ` ${config.unit}` : ''}`}</span>
                            </span>
                        )} */}
                    </div>
                    {hasImportance && (
                        <Dropdown
                            selectedOption={{
                                id: formData[importanceKey],
                                label: formData[importanceKey],
                            }}
                            listStyles={{ minWidth: '0' }}
                            style={{ width: '30%', height: '44px' }}
                            placeholder={'Importance'}
                            hasError={hasError}
                            errorMessage={'Required'}
                            optionsList={IMPORTANCE_LEVELS}
                            itemClickHandler={(option) => handleChange(importanceKey, option.id)}
                            disabled={!formData[formKey]}
                        />
                    )}
                </div>
            );
        }

        case CONFIG_TYPE.MULTIPLE_SELECT: {
            const isValid = formData[formKey] && !formData[importanceKey];
            const isAllDone =
                !formData[formKey] && formData[importanceKey] && formData[importanceKey] !== PEC_IMPORTANCE_CONSTANTS.NOT_NEEDED;
            const selectedOptions = (formData[formKey] as string[]) || [];
            const options = (config.dropdownOptions || []).map((opt: any) => ({
                id: opt._id,
                label: opt.label,
            }));

            const isValidSelection = selectedOptions.every((selectedId) => options.some((option) => option.id === selectedId));
            return (
                <div className={styles.Dflex} style={{ width: '100%' }}>
                    <MultiSelectDropdown
                        hasError={isAllDone}
                        errorMessage={'Required'}
                        WrapperStyle={hasImportance ? { width: '70%', height: '44px' } : { width: '100%', height: '44px' }}
                        selectedOptions={isValidSelection ? selectedOptions : []}
                        options={options}
                        placeholder={config.isRequired ? config.placeholder : `${config.placeholder} `}
                        onChange={(selectedOptions) => {
                            handleChange(formKey, selectedOptions, hasImportance ? importanceKey : null);
                            if (selectedOptions.length === 0 && hasImportance && formData[importanceKey]) handleChange(importanceKey, '');
                        }}
                        disabled={dealDrawerState?.readonly}
                        showFloatingLabel={showFloatingLabel}
                    />
                    {hasImportance && (
                        <Dropdown
                            selectedOption={{
                                id: formData[importanceKey],
                                label: formData[importanceKey],
                            }}
                            listStyles={{ minWidth: '0' }}
                            style={{ width: '30%', height: '44px' }}
                            placeholder={'Importance'}
                            hasError={isValid}
                            errorMessage={'Required'}
                            optionsList={IMPORTANCE_LEVELS}
                            itemClickHandler={(option) => handleChange(importanceKey, option.id)}
                            disabled={!selectedOptions?.length}
                        />
                    )}
                </div>
            );
        }

        case CONFIG_TYPE.MULTI_MULTIPLE_SELECT: {
            /*
                This is a combination of multiple MutiSelectDropdowns 
                and a single Dropdown for importance, click the + button 
                to add more MultiSelectDropdowns. Max fields can be set in the config.
            */

            const options = (config.dropdownOptions || []).map((opt: any) => ({
                id: opt._id,
                label: opt.label,
            }));

            // Check if the last field is empty
            const isLastFieldEmpty = formData[formKey]?.[formData[formKey].length - 1]?.length === 0;

            return (
                <div>
                    {(formData[formKey] || [[]]).map((selectedOptions, index) => (
                        <div className={styles.DflexMutiDropDowns} key={index}>
                            <div className={styles.OrContainer}>{index > 0 && <span>or</span>}</div>
                            <MultiSelectDropdown
                                hasError={config.isRequired && selectedOptions.length === 0}
                                errorMessage={'Required'}
                                WrapperStyle={hasImportance ? { width: '70%' } : { width: '100%' }}
                                selectedOptions={selectedOptions}
                                options={options}
                                placeholder={index === 0 ? (config.isRequired ? config.placeholder : `${config.placeholder} `) : ''}
                                onChange={(newSelectedOptions) => {
                                    const updatedSelections = [...(formData[formKey] || [])];
                                    updatedSelections[index] = newSelectedOptions;
                                    const filteredSelections =
                                        updatedSelections.length > 1
                                            ? updatedSelections.filter((selection, idx) => idx === 0 || selection.length > 0)
                                            : updatedSelections;

                                    handleChange(formKey, filteredSelections, hasImportance && index === 0 ? importanceKey : null);

                                    if (index === 0 && newSelectedOptions.length === 0 && hasImportance && formData[importanceKey])
                                        handleChange(importanceKey, '');
                                }}
                                disabled={dealDrawerState?.readonly}
                                showFloatingLabel={showFloatingLabel}
                            />

                            {hasImportance && index === 0 && (
                                <Dropdown
                                    selectedOption={{
                                        id: formData[importanceKey],
                                        label: formData[importanceKey],
                                    }}
                                    listStyles={{ minWidth: '0' }}
                                    style={{ width: '30%', height: '44px' }}
                                    placeholder={'Importance'}
                                    hasError={!formData[importanceKey]}
                                    errorMessage={'Required'}
                                    optionsList={IMPORTANCE_LEVELS}
                                    itemClickHandler={(option) => handleChange(importanceKey, option.id)}
                                    disabled={!selectedOptions[0]?.length}
                                />
                            )}
                        </div>
                    ))}

                    {formData[formKey]?.length < config.maxFields && (
                        <Button
                            text={config.ctaText}
                            type={'button'}
                            onClick={(e) => {
                                e.preventDefault();
                                /*Add a new empty array to formData[formKey] to render a 
                                    new MultiSelectDropdown if Last Fields is not empty */
                                if (!isLastFieldEmpty) {
                                    handleChange(formKey, [...(formData[formKey] || []), []]);
                                }
                            }}
                            variant={'white'}
                            disabled={isLastFieldEmpty}
                            style={{
                                marginTop: '1.5rem',
                                border: 'none',
                                outline: 'none',
                                color: isLastFieldEmpty ? 'rgba(203, 203, 203, 1)' : 'rgba(96, 33, 179, 1)',
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '0.3rem',
                                padding: '0',
                                cursor: isLastFieldEmpty ? 'not-allowed' : 'pointer',
                                backgroundColor: 'transparent',
                            }}
                            icon={
                                <PlusIcon
                                    colour={isLastFieldEmpty ? 'rgba(203, 203, 203, 1)' : 'rgba(96, 33, 179, 1)'}
                                    width={'12'}
                                    height={'12'}
                                />
                            }
                        />
                    )}
                </div>
            );
        }

        case CONFIG_TYPE.MULTI_MULTIPLE_SELECT_WITH_SEARCH: {
            /*
                This is a combination of multiple MutiSelectDropdowns 
                and a single Dropdown for importance, click the + button 
                to add more MultiSelectDropdowns. Max fields can be set in the config.
            */
            let isValid = formData[formKey] && !formData[importanceKey];
            const options = useMemo(() => {
                return (config?.dropdownOptions || []).map((opt: any) => ({
                    label: opt.label,
                    value: opt.label,
                }));
            }, [config?.dropdownOptions]);

            // Check if the last field is empty
            const isLastFieldEmpty = formData[formKey]?.[formData[formKey].length - 1]?.length === 0;

            return (
                <div>
                    {(formData[formKey] || [[]]).map((selectedOptions, index) => (
                        <div className={styles.DflexMutiDropDowns} key={index}>
                            <div className={styles.OrContainer}>{index > 0 && <span>or</span>}</div>
                            <MultiSelectSearchDropdown
                                options={options}
                                selectedItems={selectedOptions}
                                placeholder={config.isRequired ? config.placeholder : `${config.placeholder} `}
                                showFloatingLabel={showFloatingLabel}
                                toggleSelection={(newSelectedOptions) => {
                                    const updatedSelections = [...(formData[formKey] || [])];
                                    updatedSelections[index] = newSelectedOptions;
                                    const filteredSelections =
                                        updatedSelections.length > 1
                                            ? updatedSelections.filter((selection, idx) => idx === 0 || selection.length > 0)
                                            : updatedSelections;

                                    handleChange(formKey, filteredSelections, hasImportance && index === 0 ? importanceKey : null);
                                    if (index === 0 && newSelectedOptions.length === 0 && hasImportance && formData[importanceKey])
                                        handleChange(importanceKey, '');
                                }}
                                wrapperStyle={index === 0 ? { width: '100%', height: '44px' } : { width: '62%', height: '44px' }}
                                selectedItemStyle={
                                    index === 0
                                        ? {}
                                        : {
                                              maxWidth: '10rem',
                                          }
                                }
                            />

                            {hasImportance && index === 0 && (
                                <Dropdown
                                    selectedOption={{
                                        id: formData[importanceKey],
                                        label: formData[importanceKey],
                                    }}
                                    listStyles={{ minWidth: '0' }}
                                    style={{ width: '45%', height: '44px' }}
                                    placeholder={'Importance'}
                                    hasError={isValid}
                                    errorMessage={'Required'}
                                    optionsList={IMPORTANCE_LEVELS}
                                    itemClickHandler={(option) => handleChange(importanceKey, option.id)}
                                    disabled={!selectedOptions[0]?.length}
                                />
                            )}
                        </div>
                    ))}

                    {formData[formKey]?.length < config.maxFields && (
                        <Button
                            text={config.ctaText}
                            type={'button'}
                            onClick={(e) => {
                                e.preventDefault();
                                /*Add a new empty array to formData[formKey] to render a 
                                    new MultiSelectDropdown if Last Fields is not empty */
                                if (!isLastFieldEmpty) {
                                    handleChange(formKey, [...(formData[formKey] || []), []]);
                                }
                            }}
                            variant={'white'}
                            disabled={isLastFieldEmpty}
                            style={{
                                marginTop: '1.5rem',
                                border: 'none',
                                outline: 'none',
                                color: isLastFieldEmpty ? 'rgba(203, 203, 203, 1)' : 'rgba(96, 33, 179, 1)',
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '0.3rem',
                                padding: '0',
                                cursor: isLastFieldEmpty ? 'not-allowed' : 'pointer',
                                backgroundColor: 'transparent',
                            }}
                            icon={
                                <PlusIcon
                                    colour={isLastFieldEmpty ? 'rgba(203, 203, 203, 1)' : 'rgba(96, 33, 179, 1)'}
                                    width={'12'}
                                    height={'12'}
                                />
                            }
                        />
                    )}
                </div>
            );
        }

        case CONFIG_TYPE.RANGE: {
            let minKey = `minimum${convertToFirstLetterCapital(fieldKey)}`;
            let maxKey = `maximum${convertToFirstLetterCapital(fieldKey)}`;
            if (sectionKey) {
                minKey = `${sectionKey}.${minKey}`;
                maxKey = `${sectionKey}.${maxKey}`;
            }
            const { minErrorMessage, maxErrorMessage, importanceMissing, minIsValid, maxIsValid } = validateRange(
                formData[minKey],
                formData[maxKey],
                hasImportance,
                formData[importanceKey],
            );
            const hideMaxRangeInput = !!(config?.hideRangeKey && config?.hideRangeKey === 'max');
            const hideMinRangeInput = !!(config?.hideRangeKey && config?.hideRangeKey === 'min');
            const showBothInputs = !(hideMaxRangeInput || hideMinRangeInput);

            const isBothAreEmpty = isEmpty(formData[minKey]) && isEmpty(formData[maxKey]);

            return (
                <div className={styles.Dflex} style={{ width: '100%' }}>
                    <div className={styles.RangeInput} style={hasImportance ? { width: '70%' } : { width: '100%' }}>
                        {!hideMinRangeInput && (
                            <Input
                                type="number"
                                allowNegative={config.allowNegative ?? false}
                                allowDecimal={config.allowDecimal ?? false}
                                value={formData[minKey] ?? ''}
                                placeholder={showBothInputs ? 'Minimum' : config.placeholder}
                                onChange={(value) => {
                                    handleChange(minKey, value);
                                    if (isEmpty(value) && isEmpty(formData[maxKey]) && hasImportance && formData[importanceKey])
                                        handleChange(importanceKey, '');
                                }}
                                unit={config.unit}
                                style={{ marginTop: 0, width: '100%', height: '44px' }}
                                unitContainer={{ minWidth: showBothInputs ? '40%' : '' }}
                                disabled={dealDrawerState?.readonly}
                                errorMessage={minErrorMessage}
                                isValid={minIsValid}
                                numberOfDecimals={2}
                                showFloatingLabel={showFloatingLabel}
                            />
                        )}
                        {!hideMaxRangeInput && !hideMinRangeInput && <span style={{ fontSize: '10px', fontWeight: 500 }}>to</span>}
                        {!hideMaxRangeInput && (
                            <Input
                                type="number"
                                allowNegative={config.allowNegative ?? false}
                                allowDecimal={config.allowDecimal ?? false}
                                value={formData[maxKey] ?? ''}
                                placeholder={showBothInputs ? 'Maximum' : config.placeholder}
                                onChange={(value) => {
                                    handleChange(maxKey, value);
                                    if (isEmpty(value) && isEmpty(formData[minKey]) && hasImportance && formData[importanceKey])
                                        handleChange(importanceKey, '');
                                }}
                                unit={config.unit}
                                style={{ marginTop: 0, width: '100%', height: '44px' }}
                                unitContainer={{ minWidth: showBothInputs ? '40%' : '' }}
                                disabled={dealDrawerState?.readonly}
                                errorMessage={maxErrorMessage}
                                isValid={maxIsValid}
                                numberOfDecimals={2}
                                showFloatingLabel={showFloatingLabel}
                            />
                        )}
                    </div>
                    {hasImportance && (
                        <Dropdown
                            selectedOption={{
                                id: formData[importanceKey],
                                label: formData[importanceKey],
                            }}
                            listStyles={{ minWidth: '0' }}
                            style={{ width: '30%', height: '44px' }}
                            placeholder={'Importance'}
                            hasError={importanceMissing}
                            errorMessage={'Required'}
                            optionsList={IMPORTANCE_LEVELS}
                            itemClickHandler={(option) => handleChange(importanceKey, option.id)}
                            disabled={isBothAreEmpty}
                        />
                    )}
                </div>
            );
        }

        case CONFIG_TYPE.RANGE_FILTER: {
            let minKey = `minimum${convertToFirstLetterCapital(fieldKey)}`;
            let maxKey = `maximum${convertToFirstLetterCapital(fieldKey)}`;
            if (sectionKey) {
                minKey = `${sectionKey}.${minKey}`;
                maxKey = `${sectionKey}.${maxKey}`;
            }
            let isDone = formData[minKey] || formData[maxKey];
            let { isValid, minErrorMessage, maxErrorMessage, importanceMissing } = validateRange(
                formData[minKey],
                formData[maxKey],
                hasImportance,
                formData[importanceKey],
            );

            return (
                <div className={isDone ? styles.DflexFiltersDone : styles.DflexFilters} style={{ width: '100%' }}>
                    <div className={styles.InputLabel} style={{ width: '100%' }}>
                        {config.label}
                        <Button
                            text={'Clear'}
                            type={'reset'}
                            onClick={(e) => {
                                e.preventDefault();
                                handleChange(minKey, '');
                                handleChange(maxKey, '');
                            }}
                            variant={'white'}
                            style={{
                                visibility: isDone ? 'visible' : 'hidden',
                                marginTop: 0,
                                border: '1px solid #E7E7E7',
                                color: '#4D4D4D',
                                fontSize: '0.75rem',
                                padding: '0.25rem 0.75rem',
                            }}
                        />
                    </div>
                    <div style={hasImportance ? { width: '70%' } : { width: '100%' }} className={styles.RangeInput}>
                        <Input
                            type="number"
                            allowNegative={config.allowNegative ?? false}
                            allowDecimal={config.allowDecimal ?? false}
                            value={formData[minKey]}
                            placeholder="Minimum"
                            onChange={(value) => {
                                if (value > 100) return;
                                handleChange(minKey, value);
                            }}
                            unit={config.unit}
                            style={{ marginTop: 0, width: '100%', height: '44px' }}
                            unitContainer={{ minWidth: '40%' }}
                            disabled={dealDrawerState?.readonly}
                            errorMessage={minErrorMessage}
                            isValid={isValid}
                            showFloatingLabel={showFloatingLabel}
                        />
                        <Input
                            type="number"
                            allowNegative={config.allowNegative ?? false}
                            allowDecimal={config.allowDecimal ?? false}
                            value={formData[maxKey]}
                            placeholder="Maximum"
                            onChange={(value) => {
                                if (value > 100) return;
                                handleChange(maxKey, value);
                            }}
                            unit={config.unit}
                            style={{ marginTop: 0, width: '100%', height: '44px' }}
                            unitContainer={{ minWidth: '40%' }}
                            disabled={dealDrawerState?.readonly}
                            errorMessage={maxErrorMessage}
                            isValid={isValid}
                            numberOfDecimals={config.numberOfDecimals}
                            showFloatingLabel={showFloatingLabel}
                        />
                    </div>
                </div>
            );
        }
        case CONFIG_TYPE.MULTIPLE_SELECT_FILTER: {
            const isDone = !!formData[formKey];
            return (
                <div className={isDone ? styles.DflexFiltersDone : styles.DflexFilters} style={{ width: '100%' }}>
                    <div className={styles.InputLabel} style={{ width: '100%' }}>
                        {config.label}
                        <Button
                            text={'Clear'}
                            type={'reset'}
                            onClick={(e) => {
                                e.preventDefault();
                                handleChange(formKey, '');
                            }}
                            variant={'white'}
                            style={{
                                visibility: isDone ? 'visible' : 'hidden',
                                marginTop: 0,
                                border: '1px solid #E7E7E7',
                                color: '#4D4D4D',
                                fontSize: '0.75rem',
                                padding: '0.25rem 0.75rem',
                            }}
                        />
                    </div>
                    <MultiSelectSearchDropdown
                        options={(config.dropdownOptions || []).map((opt: any) => ({
                            label: opt.label,
                            value: opt._id,
                        }))}
                        selectedItems={(formData[formKey] as string[]) || []}
                        placeholder={'All'}
                        toggleSelection={(selectedOptions) => {
                            handleChange(formKey, selectedOptions.length > 0 ? selectedOptions : '');
                        }}
                    />
                </div>
            );
        }
        case CONFIG_TYPE.MULTIPLE_SELECT_API_FILTER: {
            const isDone = formData[formKey];
            return (
                <div className={isDone ? styles.DflexFiltersDone : styles.DflexFilters} style={{ width: '100%' }}>
                    <div className={styles.InputLabel} style={{ width: '100%' }}>
                        {config.label}
                        <Button
                            text={'Clear'}
                            type={'reset'}
                            onClick={(e) => {
                                e.preventDefault();
                                handleChange(formKey, '');
                            }}
                            variant={'white'}
                            style={{
                                visibility: isDone ? 'visible' : 'hidden',
                                marginTop: 0,
                                border: '1px solid #E7E7E7',
                                color: '#4D4D4D',
                                fontSize: '0.75rem',
                                padding: '0.25rem 0.75rem',
                            }}
                        />
                    </div>
                    <MultiSelectSearchDropdown
                        options={options}
                        selectedItems={(formData[formKey] as string[]) || []}
                        placeholder={'All'}
                        showFloatingLabel={showFloatingLabel}
                        toggleSelection={(selectedOptions) => {
                            if (selectedOptions.length > 0) {
                                handleChange(formKey, selectedOptions);
                            } else {
                                handleChange(formKey, '');
                            }
                        }}
                    />
                </div>
            );
        }

        case CONFIG_TYPE.MULTIPLE_SELECT_API_FILTER_INLINE:
            const isNegativeLenderList = formKey === 'dealTerms.negativeLenderList';
            return (
                <MultiSelectSearchDropdown
                    options={options}
                    selectedItems={
                        isNegativeLenderList
                            ? negativeLenderList
                                  ?.filter((lender) => {
                                      const selectedIds = (formData[formKey] as string[]) || [];
                                      if (Array.isArray(lender.value)) {
                                          return lender.value.some((id) => selectedIds.includes(id));
                                      } else {
                                          return selectedIds.includes(lender.value);
                                      }
                                  })
                                  .map((lender) => lender.label) || []
                            : (formData[formKey] as string[]) || []
                    }
                    placeholder={config.placeholder}
                    wrapperStyle={{ height: '44px' }}
                    showFloatingLabel={showFloatingLabel}
                    toggleSelection={(selectedOptions) => {
                        if (selectedOptions.length > 0) {
                            if (isNegativeLenderList) {
                                const allSelectedLenderIds: string[] = [];
                                for (const selectedOption of selectedOptions) {
                                    const selectedLender = negativeLenderList?.find((item) => item.label === selectedOption);
                                    if (selectedLender) {
                                        if (Array.isArray(selectedLender.value)) {
                                            allSelectedLenderIds.push(...selectedLender.value);
                                        } else {
                                            allSelectedLenderIds.push(selectedLender.value);
                                        }
                                    }
                                }
                                handleChange(formKey, allSelectedLenderIds);
                            } else {
                                handleChange(formKey, selectedOptions);
                            }
                        } else {
                            handleChange(formKey, '');
                        }
                    }}
                />
            );

        case CONFIG_TYPE.MULTIPLE_SELECT_WITH_SEARCH: {
            let isValid = formData[formKey] && !formData[importanceKey];
            const configOptions = useMemo(() => {
                return (config?.dropdownOptions || []).map((opt: any) => ({
                    label: opt.label,
                    value: opt.label,
                }));
            }, [config?.dropdownOptions]);
            const dropdownOptions = configOptions.length > 0 ? configOptions : options;
            return (
                <div className={styles.Dflex} style={{ width: '100%' }}>
                    <MultiSelectSearchDropdown
                        options={dropdownOptions}
                        selectedItems={(formData[formKey] as string[]) || []}
                        placeholder={config.placeholder}
                        wrapperStyle={hasImportance ? { width: '70%', height: '44px' } : { width: '100%', height: '44px' }}
                        showFloatingLabel={showFloatingLabel}
                        toggleSelection={(selectedOptions) => {
                            if (selectedOptions.length > 0) {
                                handleChange(formKey, selectedOptions);
                            } else {
                                handleChange(formKey, '', hasImportance ? importanceKey : null);
                            }
                        }}
                    />
                    {hasImportance && (
                        <Dropdown
                            selectedOption={{
                                id: formData[importanceKey],
                                label: formData[importanceKey],
                            }}
                            listStyles={{ minWidth: '0' }}
                            placeholder={'Importance'}
                            style={{ width: '30%', height: '44px' }}
                            hasError={isValid}
                            errorMessage={'Required'}
                            optionsList={IMPORTANCE_LEVELS}
                            itemClickHandler={(option) => handleChange(importanceKey, option.id)}
                            disabled={!formData[formKey]}
                        />
                    )}
                </div>
            );
        }

        case CONFIG_TYPE.MULTIPLE_SELECT_API: {
            // This fetches data from an API (e.g., sector list or loan type options).
            // We assume you have them in `loanTypeOptions` or `sectorList`.
            let isValid = formData[formKey] && !formData[importanceKey];
            let isAllDone =
                !formData[formKey] && formData[importanceKey] && formData[importanceKey] !== PEC_IMPORTANCE_CONSTANTS.NOT_NEEDED;

            let options: { id: string; label: string }[] = [];
            // Example logic: if BORROWER_PROFILE => sectorList
            // if deal or preferred => loanTypeOptions, etc.
            if (sectionKey === CONFIG_STEP.preferredTerms || sectionKey === CONFIG_STEP.dealTerms) {
                options = Object.values(loanTypeOptions || []).map((item: any) => ({
                    id: item.productName,
                    label: item.productName,
                }));
            } else if (fieldKey === 'negativeSector') {
                options =
                    negativeSectorList?.map((sec: any) => ({
                        id: sec._id,
                        label: sec.label,
                    })) ?? [];
            } else if (sectionKey === CONFIG_STEP.borrowerProfile) {
                options =
                    sectorList?.map((sec: any) => ({
                        id: sec._id,
                        label: sec.label,
                    })) ?? [];
            }
            const selectedOptions = (formData[formKey] as string[]) || [];
            const isValidSelection = selectedOptions?.every((selectedId) => options.some((option) => option.id === selectedId));
            return (
                <div className={styles.Dflex} style={{ width: '100%' }}>
                    <MultiSelectDropdown
                        hasError={isAllDone}
                        errorMessage={'Required'}
                        WrapperStyle={hasImportance ? { width: '70%' } : { width: '100%' }}
                        selectedOptions={isValidSelection ? selectedOptions : []}
                        options={options}
                        placeholder={config.isRequired ? config.placeholder : `${config.placeholder}  `}
                        onChange={(selectedOptions) => {
                            handleChange(formKey, selectedOptions, hasImportance ? importanceKey : null);
                            if (selectedOptions.length === 0 && hasImportance && formData[importanceKey]) handleChange(importanceKey, '');
                        }}
                        disabled={dealDrawerState?.readonly}
                        showFloatingLabel={showFloatingLabel}
                    />
                    {hasImportance && (
                        <Dropdown
                            selectedOption={{
                                id: formData[importanceKey],
                                label: formData[importanceKey],
                            }}
                            listStyles={{ minWidth: '0' }}
                            style={{ width: '30%', height: '44px' }}
                            placeholder={'Importance'}
                            hasError={isValid}
                            errorMessage={'Required'}
                            optionsList={IMPORTANCE_LEVELS}
                            itemClickHandler={(option) => handleChange(importanceKey, option.id)}
                            disabled={!selectedOptions?.length}
                        />
                    )}
                </div>
            );
        }

        case CONFIG_TYPE.DROPDOWN_WITH_API: {
            const isValid =
                !!formData[formKey] && !formData[importanceKey] && formData[importanceKey] !== PEC_IMPORTANCE_CONSTANTS.NOT_NEEDED;

            let options: { id: string; label: string }[] = [];

            // To fetch data of Company Sector List from API
            if (sectionKey === CONFIG_STEP.borrowerProfile) {
                options =
                    sectorList?.map((sec: any) => ({
                        id: sec._id,
                        label: sec.label,
                    })) ?? [];
            }

            const selectedOptions = (formData[formKey] as string) || '';

            return (
                <div className={styles.Dflex} style={{ width: '100%' }}>
                    <Dropdown
                        enableSearch={config.search}
                        selectedOption={{
                            id: selectedOptions,
                            label: selectedOptions,
                        }}
                        optionsList={options}
                        placeholder={config.isRequired ? config.placeholder : `${config.placeholder} `}
                        label={config.isRequired ? config.placeholder : `${config.placeholder} `}
                        itemClickHandler={(option) => {
                            handleChange(formKey, option.id, hasImportance ? importanceKey : null);
                            if (!option.id && hasImportance && formData[importanceKey]) handleChange(importanceKey, '');
                        }}
                        style={{ width: '100%', height: '44px' }}
                        disabled={dealDrawerState?.readonly}
                        errorMessage={config.errorMessage}
                    />

                    {hasImportance && (
                        <Dropdown
                            selectedOption={{
                                id: formData[importanceKey],
                                label: formData[importanceKey],
                            }}
                            listStyles={{ minWidth: '0' }}
                            style={{ width: '30%', height: '44px' }}
                            placeholder={'Importance'}
                            hasError={isValid}
                            errorMessage={'Required'}
                            optionsList={IMPORTANCE_LEVELS}
                            itemClickHandler={(option) => handleChange(importanceKey, option.id)}
                            disabled={!formData[formKey]}
                        />
                    )}
                </div>
            );
        }

        case CONFIG_TYPE.INPUT_WITH_MODAL_BUTTON: {
            const isWebsiteField = formKey === 'borrowerProfile.website';
            return (
                <div className={styles.Dflex} style={{ width: '100%' }}>
                    <div className={styles.DflexColumn} style={{ width: '100%', gap: '0.25rem', position: 'relative' }}>
                        <Input
                            key={fieldKey}
                            type={config.type}
                            allowNegative={config.allowNegative ?? false}
                            allowDecimal={config.allowDecimal ?? false}
                            value={formData[formKey] ?? ''}
                            placeholder={config.isRequired ? config.placeholder : `${config.placeholder}  `}
                            onChange={(value) => handleChange(formKey, value)}
                            unit={config.unit}
                            style={
                                hasImportance
                                    ? { width: '70%', marginTop: 0, height: '44px' }
                                    : { width: '100%', marginTop: 0, height: '44px' }
                            }
                            unitContainer={{ paddingRight: '8.125rem' }}
                            unitStyle={{ paddingLeft: '3.125rem' }}
                            showFloatingLabel={showFloatingLabel}
                            disabled={dealDrawerState?.readonly || (isWebsiteField && isWebsitePrefilled)}
                            errorMessage={
                                isWebsiteField ? (isWebsitePrefilled ? '' : config?.errorMessage || '') : config?.errorMessage || ''
                            }
                            isValid={
                                isWebsiteField
                                    ? isWebsitePrefilled
                                        ? true
                                        : getInputValidation?.(formData[formKey], formKey)
                                    : getInputValidation?.(formData[formKey], formKey)
                            }
                        />
                        {config?.showButton && (!isWebsiteField || !isWebsitePrefilled) && (
                            <div
                                style={{
                                    position: 'absolute',
                                    right: 0,
                                    bottom: auditedfinancialsMapping[formKey] ? '15px' : '-4px',
                                    padding: '0.375rem',
                                }}
                            >
                                <Button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (config?.buttonText == 'verifyWebsite') {
                                            validateWebsite?.(formData[formKey]);
                                        } else {
                                            setShowModal(true);
                                            Mixpanel.track(ESTIMATE_METRIC, {
                                                Metric: fieldKey,
                                                Email: user.userData.primaryEmail,
                                                pnoId: user.userData.investorOrgId,
                                                Current_url: window.location.href,
                                                Company_Name: drawerContent.subHeading,
                                            });
                                        }
                                    }}
                                    text={config?.buttonText == 'verifyWebsite' ? getButtonText?.() : config?.buttonText}
                                    icon={getButtonIcon?.(config?.buttonImg)}
                                    iconStyles={{ paddingTop: '0.08rem' }}
                                    style={{
                                        backgroundColor: isWebsiteField
                                            ? getButtonText?.() === 'Verified'
                                                ? '#E1FFE1'
                                                : '#F9F5FF'
                                            : '#F9F5FF',
                                        color: isWebsiteField ? (getButtonText?.() === 'Verified' ? '#49A71B' : '#6021B3') : '#6021B3',
                                        borderRadius: '4px',
                                        fontSize: '0.75rem',
                                        border: 'none',
                                        padding: '0.375rem',
                                        width: '5.6875rem',
                                        height: '2.5rem',
                                    }}
                                />
                            </div>
                        )}
                        {/* {auditedfinancialsMapping[formKey] && (
                            <span className={styles.financialYearLabel}>
                                {auditedfinancialsMapping[formKey]?.year
                                    ? `Audited Data FY'${getFiscalYearSuffix(auditedfinancialsMapping[formKey].year)}: `
                                    : 'AICA Report: '}
                                <span>{`${auditedfinancialsMapping[formKey]?.value ?? ''}${config.unit ? ` ${config.unit}` : ''}`}</span>
                            </span>
                        )} */}
                    </div>
                    {showModal && (
                        <RenderModal
                            show={showModal}
                            formKey={formKey}
                            onClose={() => setShowModal(false)}
                            modalDetails={config?.modalDetails}
                            formData={formData}
                            sectionKey={sectionKey}
                            handleChange={handleChange}
                            auditedfinancialsMapping={auditedfinancialsMapping}
                            dealDrawerState={dealDrawerState}
                            activeFinancialsTab={activeFinancialsTab}
                            sectionDualDataStorage={sectionDualDataStorage}
                        />
                    )}
                </div>
            );
        }

        case CONFIG_TYPE.MULTIPLE_SELECT_WITH_INPUT: {
            // Refer to DynamicFieldsRenderer.tsx for the implementation of this Input Renderer on chosen section.
            const isAllDone = !formData[formKey] && formData[importanceKey];
            const selectedOptions = formData[formKey]?.map((item: { type: string }) => item.type) || [];
            const options = (config?.dropdownOptions || []).map((opt: any) => ({
                id: opt._id,
                label: opt.label,
            }));

            return (
                <div className={styles.DflexColumn}>
                    <MultiSelectDropdown
                        hasError={isAllDone}
                        errorMessage={'Required'}
                        WrapperStyle={hasImportance ? { width: '70%' } : { width: '100%' }}
                        selectedOptions={selectedOptions}
                        options={options}
                        placeholder={config.isRequired ? config.placeholder : `${config.placeholder}  `}
                        disabled={dealDrawerState?.readonly}
                        showFloatingLabel={showFloatingLabel}
                        onChange={(selected: string[]) => {
                            const updatedFormData = selected.map((type) => {
                                const existing = formData[formKey]?.find((item: any) => item.type === type);
                                return existing || { type, value: '' };
                            });
                            handleChange(formKey, updatedFormData);
                        }}
                    />
                </div>
            );
        }

        case CONFIG_TYPE.MULTIPLE_SELECT_WITH_INPUT_AND_SEARCH: {
            // This is a combination of a dropdown values and the corresponding input field
            /*
             * Refer to DynamicFieldsRenderer.tsx for the implementation of this Input Renderer on chosen section.
             */
            const { dropdownInputOptions, inputOptionsData } = React.useMemo(() => {
                const dropdownToPlaceholder = new Map<string, string>();
                const placeholderToValue = new Map<string, string>();
                return { dropdownInputOptions: dropdownToPlaceholder, inputOptionsData: placeholderToValue };
            }, []);
            const selectedOptions = formData[formKey] || [];
            const options = useMemo(() => {
                return (config?.dropdownOptions || []).map((opt: any) => ({
                    id: opt._id,
                    label: opt.label,
                    value: opt.label,
                }));
            }, [config?.dropdownOptions]);

            return (
                <div className={styles.DflexColumn}>
                    <MultiSelectSearchDropdown
                        options={options}
                        selectedItems={(formData[formKey]?.map((item) => item.type) as string[]) || []}
                        placeholder={config.isRequired ? config.placeholder : `${config.placeholder} `}
                        showFloatingLabel={showFloatingLabel}
                        toggleSelection={(selectedOptions) => {
                            const validOptions = selectedOptions.filter((v): v is string => v !== null);
                            if (validOptions.length > 0) {
                                const updatedFormData = validOptions.map((item) => {
                                    const existing = formData[formKey]?.find((selectedItem: any) => selectedItem.type === item);
                                    const value =
                                        dropdownInputOptions.get(item) && inputOptionsData.get(dropdownInputOptions.get(item) || '')
                                            ? inputOptionsData.get(dropdownInputOptions.get(item) || '')
                                            : '';
                                    return existing || { type: item, value: value };
                                });
                                handleChange(formKey, updatedFormData);
                            } else {
                                handleChange(formKey, '');
                            }
                        }}
                    />

                    {config?.dropdownOptions?.map((opt: any) => {
                        const inputObj = opt.inputObj;
                        if (inputObj?.placeholder) {
                            dropdownInputOptions.set(opt.label, inputObj.placeholder);
                            if (formData[formKey]?.find((item: any) => item.type === opt.label)?.value || '') {
                                inputOptionsData.set(
                                    inputObj.placeholder,
                                    formData[formKey]?.find((item: any) => item.type === opt.label)?.value || '',
                                );
                            }
                        }
                        return null;
                    })}
                </div>
            );
        }

        case CONFIG_TYPE.DROPDOWN_WITH_CUSTOM_COMPONENT: {
            const isValid = formData[formKey] && !formData[importanceKey];
            const CUSTOM_COMPONENT = CUSTOM_COMPONENT_MAP[config.component];
            return (
                <div className={styles.Dflex} style={{ width: '100%' }}>
                    <div className={styles.DflexColumn} style={{ width: '100%', gap: '0.25rem', position: 'relative' }}>
                        <CUSTOM_COMPONENT
                            placeholder={config.isRequired ? config.placeholder : `${config.placeholder} `}
                            value={formData[formKey] ?? ''}
                            onChange={(value) => {
                                handleChange(formKey, value, hasImportance ? importanceKey : null);
                                if (isEmpty(value) && hasImportance && formData[importanceKey]) handleChange(importanceKey, '');
                            }}
                            errorMessage={config?.errorMessage ?? ''}
                            isValid={isValid}
                            disabled={dealDrawerState?.readonly}
                            style={{ marginTop: 0 }}
                            optionsStyle={{
                                width: '100%',
                                maxHeight: '230px',
                                marginTop: '0.5rem',
                            }}
                        />
                    </div>

                    {hasImportance && (
                        <Dropdown
                            selectedOption={{
                                id: formData[importanceKey],
                                label: formData[importanceKey],
                            }}
                            listStyles={{ minWidth: '0' }}
                            style={{ width: '30%', height: '44px' }}
                            label={'Importance'}
                            placeholder={'Importance'}
                            optionsList={IMPORTANCE_LEVELS}
                            itemClickHandler={(option) => handleChange(importanceKey, option.id)}
                            disabled={!formData[formKey]}
                        />
                    )}
                </div>
            );
        }

        case CONFIG_TYPE.INVESTOR_TOGGLE:
        case CONFIG_TYPE.RECUR_INVESTOR_TOGGLE: {
            // Example using InvestorDealShare
            return (
                <InvestorDealShare
                    source={config.type}
                    lenderList={lenderList ?? []}
                    networkLabel={config.placeholder}
                    totalInvestors={lenderList?.length ?? 0}
                    showInvestorList={config.type === CONFIG_TYPE.INVESTOR_TOGGLE}
                    selectedInvestors={(formData.dealReceiverIds as string[]) || []}
                    updateSelectedInvestors={(shared) => handleChange('dealReceiverIds', shared)}
                    style={{ marginTop: '-24px' }}
                />
            );
        }

        case CONFIG_TYPE.UPLOAD: {
            // Example using UploadCard
            const existingFiles = (formData[formKey] || []).filter(
                (doc: any) => doc.documentName?.includes(UPLOAD_FILES_PREFIXES.ATTACHMENT_PREFIX),
            );

            return (
                <UploadCard
                    assesseeOrgId={dealDrawerState?.companyId as string}
                    categoryId={config?.documents?.components?.[0]?.categoryId}
                    flowId={16} // or pass from hook
                    existingFiles={existingFiles.map((doc: any) => ({
                        docId: doc.docId,
                        docName: doc.documentName,
                    }))}
                    metadataUpdate={(fileInfo) =>
                        handleChange(formKey, [
                            ...(formData[formKey] || []),
                            {
                                docId: fileInfo.docId,
                                documentName: `ATTACHMENT_${fileInfo.fileName}`,
                                action: 'SEND_TO_INVESTOR',
                            },
                        ])
                    }
                    fileDeleteHandler={(fileInfo) =>
                        handleChange(
                            formKey,
                            (formData[formKey] || []).filter((doc: any) => doc.docId !== fileInfo.docId),
                        )
                    }
                    allowMultiple
                    disabled={dealDrawerState?.readonly}
                    disableDelete={dealDrawerState?.readonly}
                    style={hasImportance ? { width: '70%' } : { width: '100%' }}
                    fileNamePrefix={UPLOAD_FILES_PREFIXES.ATTACHMENT_PREFIX}
                    maxUploadSizeMb={config?.documents?.components?.[0]?.maxUploadSizeMb}
                />
            );
        }

        case CONFIG_TYPE.BUTTON: {
            // If you have custom button components or actions
            return (
                <DealDrawerButtonComponents
                    componentKey={fieldKey}
                    formData={formData}
                    source={source}
                    categoryId={config?.documents?.components?.[0]?.categoryId}
                    handleChange={handleChange}
                />
            );
        }

        case CONFIG_TYPE.READ_ONLY_COMPARE_NUMBER: {
            // Support secondaryConfig for looking up audited values with different field names
            const secondaryFormKey = config?.secondaryConfig
                ? sectionKey
                    ? `${sectionKey}.${config?.secondaryConfig}`
                    : config.secondaryConfig
                : formKey.replace(activeFinancialsTab + '.', '');

            const value = auditedfinancialsMapping?.[secondaryFormKey]?.value;
            const displayValue = value !== undefined && value !== null ? Number(value).toFixed(config?.numberOfDecimals ?? 1) : '-';

            return (
                <div className={styles.Dflex} style={{ width: '100%' }}>
                    <div className={styles.ComparisonContainer} style={{ width: hasImportance ? '70%' : '100%' }}>
                        {/* Audited Financial Data (Read-only) */}
                        <div className={styles.ComparisonInput}>
                            <Input
                                key={`${fieldKey}-audited`}
                                type={config.type}
                                allowNegative={config.allowNegative ?? false}
                                allowDecimal={config.allowDecimal ?? false}
                                allowZero={config.allowZero ?? true}
                                value={displayValue}
                                onChange={() => {}}
                                style={{ width: '100%', marginTop: 0, height: '44px' }}
                                disabled={true}
                                readOnly={true}
                                showFloatingLabel={showFloatingLabel}
                            />
                            {/* <span className={styles.financialYearLabel}>
                                {auditedfinancialsMapping[formKey]?.year
                                    ? `Audited Data FY'${getFiscalYearSuffix(auditedfinancialsMapping[formKey].year)}`
                                    : 'AICA Report'}
                            </span> */}
                        </div>

                        {/* User Input Field */}
                        <div className={styles.ComparisonInput}>
                            <Input
                                key={fieldKey}
                                type={config.type}
                                allowNegative={config.allowNegative ?? false}
                                allowDecimal={config.allowDecimal ?? false}
                                allowZero={config.allowZero ?? true}
                                value={formData[formKey] ?? ''}
                                placeholder={config.isRequired ? config.placeholder : `${config.placeholder}`}
                                onChange={(value) => {
                                    handleChange(formKey, value, hasImportance ? importanceKey : null);
                                    if (isEmpty(value) && hasImportance && formData[importanceKey]) handleChange(importanceKey, '');
                                }}
                                style={{ width: '100%', marginTop: 0, height: '44px' }}
                                disabled={dealDrawerState?.readonly}
                                numberOfDecimals={config.numberOfDecimals}
                                errorMessage={config?.errorMessage || ''}
                                convertedText={config.convertToWord ? getConvertedValue?.(formData[formKey], config.unit) : ''}
                                isValid={getInputValidation?.(formData[formKey], formKey)}
                                showFloatingLabel={showFloatingLabel}
                            />
                        </div>
                    </div>
                    {hasImportance && (
                        <Dropdown
                            selectedOption={{
                                id: formData[importanceKey],
                                label: formData[importanceKey],
                            }}
                            listStyles={{ minWidth: '0' }}
                            style={{ width: '30%', height: '44px' }}
                            label={'Importance'}
                            placeholder={'Importance'}
                            optionsList={IMPORTANCE_LEVELS}
                            itemClickHandler={(option) => handleChange(importanceKey, option.id)}
                            disabled={!formData[formKey]}
                        />
                    )}
                </div>
            );
        }

        case CONFIG_TYPE.READ_ONLY_COMPARE_INPUT_WITH_MODAL_BUTTON: {
            const isEditable = config.isEditable ?? true;
            const isWebsiteField = formKey === 'borrowerProfile.website';
            // Support secondaryConfig for looking up audited values with different field names
            const secondaryFormKey = config?.secondaryConfig
                ? sectionKey
                    ? `${sectionKey}.${config?.secondaryConfig}`
                    : config.secondaryConfig
                : formKey.replace(activeFinancialsTab + '.', '');

            const value = auditedfinancialsMapping?.[secondaryFormKey]?.value;
            return (
                <div className={styles.Dflex} style={{ width: '100%' }}>
                    <div className={styles.ComparisonContainer} style={{ width: hasImportance ? '70%' : '100%' }}>
                        {/* Audited Financial Data (Read-only) */}

                        <div className={styles.ComparisonInput}>
                            <Input
                                key={`${fieldKey}-audited`}
                                type={config.type}
                                allowNegative={config.allowNegative ?? false}
                                allowDecimal={config.allowDecimal ?? false}
                                value={value !== undefined && value !== null ? Number(value).toFixed(config?.numberOfDecimals ?? 1) : '-'}
                                onChange={() => {}}
                                style={{ width: '100%', marginTop: 0 }}
                                disabled={true}
                                readOnly={true}
                                showFloatingLabel={showFloatingLabel}
                            />
                            {/* <span className={styles.financialYearLabel}>
                                {auditedfinancialsMapping[formKey]?.year
                                    ? `Audited Data FY'${getFiscalYearSuffix(auditedfinancialsMapping[formKey].year)}`
                                    : 'AICA Report'}
                            </span> */}
                        </div>

                        {/* User Input Field with Modal Button */}
                        <div className={styles.ComparisonInput} style={{ position: 'relative', width: '48%' }}>
                            <Input
                                key={fieldKey}
                                type={config.type}
                                allowNegative={config.allowNegative ?? false}
                                allowDecimal={config.allowDecimal ?? false}
                                value={formData[formKey] ?? ''}
                                placeholder={config.isRequired ? config.placeholder : `${config.placeholder} `}
                                onChange={(value) => {
                                    if (isEditable) {
                                        handleChange(formKey, value, hasImportance ? importanceKey : null);
                                        if (isEmpty(value) && hasImportance && formData[importanceKey]) handleChange(importanceKey, '');
                                    }
                                }}
                                style={{
                                    width: '100%',
                                    marginTop: 0,
                                    cursor: !isEditable ? 'pointer' : 'text',
                                    backgroundColor: !isEditable ? '#F5F5F5' : 'white',
                                    borderRadius: '6px',
                                }}
                                unitStyle={{ paddingLeft: '3.125rem' }}
                                numberOfDecimals={config.numberOfDecimals}
                                disabled={!isEditable || dealDrawerState?.readonly || (isWebsiteField && isWebsitePrefilled)}
                                showFloatingLabel={showFloatingLabel}
                                errorMessage={
                                    isWebsiteField ? (isWebsitePrefilled ? '' : config?.errorMessage || '') : config?.errorMessage || ''
                                }
                                isValid={
                                    isWebsiteField
                                        ? isWebsitePrefilled
                                            ? true
                                            : getInputValidation?.(formData[formKey], formKey)
                                        : getInputValidation?.(formData[formKey], formKey)
                                }
                                onClick={() => {
                                    // If not editable (calculated field), clicking the input opens the modal
                                    if (!isEditable && !dealDrawerState?.readonly) {
                                        setShowModal(true);
                                        Mixpanel.track(ESTIMATE_METRIC, {
                                            Metric: fieldKey,
                                            Email: user.userData.primaryEmail,
                                            pnoId: user.userData.investorOrgId,
                                            Current_url: window.location.href,
                                            Company_Name: drawerContent.subHeading,
                                        });
                                    }
                                }}
                            />
                            {config?.showButton && (!isWebsiteField || !isWebsitePrefilled) && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        right: 0,
                                        bottom: '0px',
                                        padding: '0.375rem',
                                    }}
                                >
                                    <Button
                                        onClick={(e) => {
                                            e.preventDefault();
                                            if (config?.buttonText == 'verifyWebsite') {
                                                validateWebsite?.(formData[formKey]);
                                            } else {
                                                setShowModal(true);
                                                Mixpanel.track(ESTIMATE_METRIC, {
                                                    Metric: fieldKey,
                                                    Email: user.userData.primaryEmail,
                                                    pnoId: user.userData.investorOrgId,
                                                    Current_url: window.location.href,
                                                    Company_Name: drawerContent.subHeading,
                                                });
                                            }
                                        }}
                                        text={
                                            config?.buttonText == 'verifyWebsite'
                                                ? getButtonText?.()
                                                : commentsThreadState.source === 'EDIT_PROFILE'
                                                ? ''
                                                : config?.buttonText
                                        }
                                        endIcon={getButtonIcon?.(config?.buttonImg)}
                                        endIconStyles={{ paddingTop: '0.08rem', marginLeft: '0.375rem' }}
                                        style={{
                                            backgroundColor: isWebsiteField
                                                ? getButtonText?.() === 'Verified'
                                                    ? '#E1FFE1'
                                                    : '#F9F5FF'
                                                : '#F9F5FF',
                                            color: isWebsiteField ? (getButtonText?.() === 'Verified' ? '#49A71B' : '#6021B3') : '#6021B3',
                                            borderRadius: '4px',
                                            fontSize: '0.75rem',
                                            border: 'none',
                                            padding: '0.375rem 0.5rem',
                                            width: commentsThreadState.source === 'EDIT_PROFILE' ? '2rem' : '5.6875rem',
                                            height: '2.2rem',
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {hasImportance && (
                        <Dropdown
                            selectedOption={{
                                id: formData[importanceKey],
                                label: formData[importanceKey],
                            }}
                            listStyles={{ minWidth: '0' }}
                            style={{ width: '30%', height: '44px' }}
                            placeholder={'Importance'}
                            optionsList={IMPORTANCE_LEVELS}
                            itemClickHandler={(option) => handleChange(importanceKey, option.id)}
                            disabled={!formData[formKey]}
                        />
                    )}

                    {showModal && (
                        <RenderModal
                            show={showModal}
                            formKey={formKey}
                            onClose={() => setShowModal(false)}
                            modalDetails={config?.modalDetails}
                            formData={formData}
                            sectionKey={sectionKey}
                            handleChange={handleChange}
                            auditedfinancialsMapping={auditedfinancialsMapping}
                            dealDrawerState={dealDrawerState}
                            activeFinancialsTab={activeFinancialsTab}
                            sectionDualDataStorage={sectionDualDataStorage}
                        />
                    )}
                </div>
            );
        }

        case CONFIG_TYPE.READ_ONLY_TOGGLE: {
            const options = (config.options || []).map((opt) => ({
                id: opt.value,
                label: opt.label,
            }));

            return (
                <div
                    className={styles.ComparisonContainer}
                    style={{ width: '100%', display: 'flex', flexDirection: 'row', alignItems: 'center' }}
                >
                    {/* Audited (read-only, always 'Audited') */}
                    <div style={{ width: '50%', display: 'flex', alignItems: 'center' }}>
                        <Input
                            key={`${fieldKey}-audited`}
                            type="text"
                            value="Audited"
                            readOnly
                            disabled
                            style={{ height: '44px', width: '100%', marginTop: '0px' }}
                            showFloatingLabel={showFloatingLabel}
                            onChange={() => {}}
                        />
                    </div>

                    {/* Data Source Selector - controls which data set to show */}
                    <div style={{ width: '50%', display: 'flex', alignItems: 'center' }}>
                        <CategorySelector
                            categoryOptions={options}
                            selectedCategory={activeFinancialsTab || formData[formKey] || ''}
                            changeSelectedTab={(val) => {
                                handleFinancialsTabChange?.(val);
                                handleChange(formKey, val);
                            }}
                            style={{
                                height: '44px',
                                width: '100%',
                                border: '1px solid #c7c7c7',
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                background: '#fff',
                                fontSize: '14px',
                                boxSizing: 'border-box',
                            }}
                            categoryStyle={{
                                flex: 1,
                                minWidth: 0,
                                justifyContent: 'center',
                                alignItems: 'center',
                                display: 'flex',
                                height: '44px',
                                padding: 0,
                            }}
                        />
                    </div>
                </div>
            );
        }
        case CONFIG_TYPE.READ_ONLY_COMPARE_INPUT_WITH_DATE: {
            // Support secondaryConfig for looking up audited values with different field names
            const secondaryFormKey = config?.secondaryConfig
                ? sectionKey
                    ? `${sectionKey}.${config?.secondaryConfig}`
                    : config.secondaryConfig
                : formKey.replace(activeFinancialsTab + '.', '');

            const value = auditedfinancialsMapping?.[secondaryFormKey]?.value;
            const dateValue = formData[formKey];
            return (
                <div className={styles.Dflex} style={{ width: '100%' }}>
                    <div className={styles.ComparisonContainer} style={{ width: hasImportance ? '70%' : '100%' }}>
                        <div className={styles.ComparisonInput}>
                            <Input
                                key={`${fieldKey}-audited`}
                                type={config.type}
                                allowNegative={config.allowNegative ?? false}
                                allowDecimal={config.allowDecimal ?? false}
                                allowZero={config.allowZero ?? true}
                                value={value !== undefined && value !== null ? value : '-'}
                                onChange={() => {}}
                                style={{ width: '100%', marginTop: 0 }}
                                disabled={true}
                                readOnly={true}
                                showFloatingLabel={showFloatingLabel}
                            />
                        </div>

                        <div className={styles.ComparisonInput}>
                            <DatePicker
                                value={dateValue}
                                onChange={(value) => {
                                    handleChange(formKey, value);
                                    if (isEmpty(value) && hasImportance && formData[importanceKey]) handleChange(importanceKey, '');
                                }}
                                placeholder={config.isRequired ? config.placeholder : `${config.placeholder} `}
                                unit={<CalendarIcon className={styles.CalendarIcon} />}
                                useFixedPosition={true}
                                calendarWidth={config.calendarWidth ?? 230}
                                showFloatingLabel={showFloatingLabel}
                                displayFormat={config.displayFormat || DATE_FORMATS.DATE_FORMAT_D_MMM_YYYY}
                                responseFormat={config.format || DATE_FORMATS.DATE_FORMAT_DDMMYYYY}
                                unitStyle={{ padding: '0.2rem 0 0.2rem 1rem' }}
                                max={config.max?.toString() ?? '0'}
                            />
                        </div>
                    </div>

                    {hasImportance && (
                        <Dropdown
                            selectedOption={{
                                id: formData[importanceKey],
                                label: formData[importanceKey],
                            }}
                            listStyles={{ minWidth: '0' }}
                            style={{ width: '30%', height: '44px' }}
                            label={'Importance'}
                            placeholder={'Importance'}
                            optionsList={IMPORTANCE_LEVELS}
                            itemClickHandler={(option) => handleChange(importanceKey, option.id)}
                            disabled={!formData[formKey]}
                        />
                    )}
                </div>
            );
        }

        case CONFIG_TYPE.READ_ONLY_COMPARE_DROPDOWN: {
            // Support secondaryConfig for looking up audited values with different field names
            const secondaryFormKey = config?.secondaryConfig
                ? sectionKey
                    ? `${sectionKey}.${config?.secondaryConfig}`
                    : config.secondaryConfig
                : formKey.replace(activeFinancialsTab + '.', '');

            const value = auditedfinancialsMapping?.[secondaryFormKey]?.value;

            return (
                <div className={styles.Dflex} style={{ width: '100%' }}>
                    <div className={styles.ComparisonContainer} style={{ width: hasImportance ? '70%' : '100%' }}>
                        {/* Audited Data (Read-only) */}
                        <div className={styles.ComparisonInput}>
                            <Input
                                key={`${fieldKey}-audited`}
                                type="text"
                                value={value !== undefined && value !== null ? value : '-'}
                                onChange={() => {}}
                                style={{ width: '100%', marginTop: 0, height: '44px' }}
                                disabled={true}
                                readOnly={true}
                                showFloatingLabel={showFloatingLabel}
                            />
                        </div>

                        {/* User Input Field - Dropdown */}
                        <div className={styles.ComparisonInput}>
                            <Dropdown
                                enableSearch={config.enableSearch ?? false}
                                selectedOption={{
                                    id: formData[formKey],
                                    label: formData[formKey],
                                }}
                                optionsList={(config.dropdownOptions || []).map((opt: any) => ({
                                    id: opt._id,
                                    label: opt.label,
                                }))}
                                placeholder={config.isRequired ? config.placeholder : `${config.placeholder} `}
                                label={config.isRequired ? config.placeholder : `${config.placeholder} `}
                                itemClickHandler={(option) => {
                                    if (!dealDrawerState?.readonly) {
                                        handleChange(formKey, option.id, hasImportance ? importanceKey : null);
                                        if (!option.id && hasImportance && formData[importanceKey]) handleChange(importanceKey, '');
                                    }
                                }}
                                style={{
                                    width: '100%',
                                    height: '44px',
                                    cursor: !dealDrawerState?.readonly ? 'default' : 'pointer',
                                    backgroundColor: !dealDrawerState?.readonly ? '#F5F5F5' : 'white',
                                }}
                                disabled={dealDrawerState?.readonly}
                                errorMessage={config?.errorMessage || ''}
                                showFloatingLabel={showFloatingLabel}
                            />
                        </div>
                    </div>

                    {hasImportance && (
                        <Dropdown
                            selectedOption={{
                                id: formData[importanceKey],
                                label: formData[importanceKey],
                            }}
                            listStyles={{ minWidth: '0' }}
                            style={{ width: '30%', height: '44px' }}
                            label={'Importance'}
                            placeholder={'Importance'}
                            optionsList={IMPORTANCE_LEVELS}
                            itemClickHandler={(option) => handleChange(importanceKey, option.id)}
                            disabled={!formData[formKey]}
                        />
                    )}
                </div>
            );
        }

        case CONFIG_TYPE.SINGLE_SELECT_DROPDOWN_WITH_SEARCH: {
            let options: { id: string; label: string }[] = [];
            if (fieldKey === 'city') {
                options =
                    citiesList?.map((city: any) => ({
                        id: city.value,
                        label: city.label,
                    })) ?? [];
            }

            return (
                <div className={styles.Dflex} style={{ width: '100%' }}>
                    <div className={styles.DflexColumn} style={{ width: hasImportance ? '70%' : '100%', gap: '0.25rem' }}>
                        <Dropdown
                            enableSearch={true}
                            selectedOption={{
                                id: formData[formKey],
                                label: formData[formKey],
                            }}
                            optionsList={options}
                            placeholder={config.isRequired ? config.placeholder : `${config.placeholder} `}
                            label={config.isRequired ? config.placeholder : `${config.placeholder} `}
                            itemClickHandler={(option) => {
                                handleChange(formKey, option.id, hasImportance ? importanceKey : null);
                            }}
                            style={{ width: '100%', height: '44px' }}
                            disabled={dealDrawerState?.readonly}
                            errorMessage={config.errorMessage}
                            showFloatingLabel={showFloatingLabel}
                        />
                    </div>
                </div>
            );
        }
        case CONFIG_TYPE.DATE: {
            return (
                <DatePicker
                    value={formData[formKey]}
                    onChange={(value) => handleChange(formKey, value)}
                    placeholder={config.isRequired ? config.placeholder : `${config.placeholder} `}
                    unit={<CalendarIcon className={styles.CalendarIcon} />}
                    useFixedPosition={true}
                    calendarWidth={config.calendarWidth ?? 294}
                    disabled={dealDrawerState?.readonly}
                />
            );
        }

        default: {
            // Basic text/number input

            let value = formData[formKey];
            if (config.isSystemGenerated && auditedfinancialsMapping?.[formKey] && auditedfinancialsMapping[formKey]?.value !== value) {
                handleChange(formKey, auditedfinancialsMapping[formKey]?.value, hasImportance ? importanceKey : null);
                value = auditedfinancialsMapping[formKey]?.value;
            }

            return (
                <div className={styles.Dflex} style={{ width: '100%' }}>
                    <div className={styles.DflexColumn} style={{ width: hasImportance ? '70%' : '100%', gap: '0.25rem' }}>
                        <Input
                            key={fieldKey}
                            type={config.type}
                            allowNegative={config.allowNegative ?? false}
                            allowDecimal={config.allowDecimal ?? false}
                            allowZero={config.allowZero ?? true}
                            allowAllCharacters={config.allowAllCharacters ?? false}
                            value={value}
                            numberOfDecimals={config.numberOfDecimals}
                            placeholder={config.isRequired ? config.placeholder : `${config.placeholder} `}
                            onChange={(value) => {
                                handleChange(formKey, value, hasImportance ? importanceKey : null);
                                if (isEmpty(value) && hasImportance && formData[importanceKey]) handleChange(importanceKey, '');
                            }}
                            unit={config.unit}
                            style={
                                hasImportance
                                    ? { width: '100%', marginTop: 0, height: '44px' }
                                    : { width: '100%', marginTop: 0, height: '44px' }
                            }
                            disabled={config?.readonly ?? dealDrawerState?.readonly}
                            errorMessage={config?.errorMessage || ''}
                            convertedText={config.convertToWord ? getConvertedValue?.(formData[formKey], config.unit) : ''}
                            isValid={getInputValidation?.(formData[formKey], formKey)}
                            showFloatingLabel={showFloatingLabel}
                        />
                        {/*Show audited financials date and its values*/}
                        {/* {auditedfinancialsMapping[formKey] && (
                            <span className={styles.financialYearLabel}>
                                {auditedfinancialsMapping[formKey]?.year
                                    && `Audited Data FY'${getFiscalYearSuffix(auditedfinancialsMapping[formKey].year)}: `
                                    : 'AICA Report: '}
                                <span>{`${auditedfinancialsMapping[formKey]?.value ?? ''}${config.unit ? ` ${config.unit}` : ''}`}</span>
                            </span>
                        )} */}
                    </div>
                    {hasImportance && (
                        <Dropdown
                            selectedOption={{
                                id: formData[importanceKey],
                                label: formData[importanceKey],
                            }}
                            listStyles={{ minWidth: '0' }}
                            style={{ width: '30%', height: '44px' }}
                            label={'Importance'}
                            placeholder={'Importance'}
                            optionsList={IMPORTANCE_LEVELS}
                            itemClickHandler={(option) => handleChange(importanceKey, option.id)}
                            disabled={!formData[formKey]}
                        />
                    )}
                </div>
            );
        }
    }
};
